// Ассеты сцены: персонажи, локации и предметы — библиотечные и свои. С 02.10 вся работа с ними — как у Artlist
// Studio: страницы разделов (/characters, /locations, /props — «Готовые / Мои / История»), страница ассета с видами,
// версии (правка создаёт новую, «Сохранить изменения» делает её текущей), кандидаты по описанию, «Загрузить своё».
//
// Одна сущность на бэке (characters.kind): /assets отдаёт библиотеку и «мои», раскладку набора под модель
// (/assets/plan — резолвер наборов) и все действия страниц.
import { api } from './client'
import { apiErrorText, PHOTO_ERROR_TEXTS, postForm } from './characters'
import type { VoiceCard } from './voiceCatalog'
import { onSceneUserChange } from './sceneSelection'

export type AssetKind = 'character' | 'location' | 'prop'

export interface AssetView {
	id: string
	role: string
	url: string | null
	/** Лёгкое превью (<key>_preview.webp); у старых фото его может не быть — onError → url. */
	preview_url: string | null
	width: number
	height: number
}

export interface Asset {
	id: string
	kind: AssetKind
	name: string
	description: string | null
	is_stock: boolean
	is_own: boolean
	/** Откуда ассет: predefined (готовый), creation_set (по описанию), upload (своя картинка), edit (копия готового
	 *  после правки), copy (дубликат), extracted (из кадра), legacy (до 02.10). */
	origin?: string | null
	/** ready — есть главный вид; building — вид рисуется; draft — видов нет. */
	status: 'ready' | 'building' | 'draft'
	ready: boolean
	avatar_url: string | null
	avatar_preview_url: string | null
	/** Плитка витрины и пикера — верх кадра «в рост» своим файлом (13.10): резкая при весе превью. Нет (старый вид до
	 *  нарезки) — плитка режет превью CSS-ом. */
	avatar_tile_url?: string | null
	/** Размер главного вида — пропорция плитки в мозаике. */
	width?: number | null
	height?: number | null
	views: AssetView[]
	has_sheet: boolean
	current_snapshot_id?: string | null
	updated_at?: string | null
	/** Голос персонажа (каталог ElevenLabs; ветка голосов) — им персонаж говорит в видео «Сцены»; null — нет. */
	voice?: VoiceCard | null
	/** Готовый персонаж: голос выбран лично (персонаж в библиотеке не менялся); stock_voice — голос библиотеки. */
	voice_chosen?: boolean
	stock_voice?: VoiceCard | null
	/** Голос подобран сервером по внешности (пол и возраст на вид), а не выбран человеком. */
	voice_auto?: boolean
	/** Набор: связанные персонажи, локации и предметы — пилюли на странице, кружки на плитке пикера. */
	links?: AssetLink[]
}

/** Связанный ассет — коротко, для пилюли и кружка. */
export interface AssetLink {
	id: string
	kind: AssetKind
	name: string
	avatar_url: string | null
	avatar_preview_url: string | null
}

/** Ракурс в версии ассета (как generation у Artlist): главный — в рост спереди или по пояс, дальше лицо, спина,
 *  профиль; у локации — главный и два вида, у предмета — спереди, сбоку, сзади. */
export type AssetAngle = 'front' | 'upper' | 'headshot' | 'back' | 'side' | 'main' | 'view_2' | 'view_3'

export interface AssetSnapshotView {
	photo_id: string
	angle: AssetAngle
	artlist_angle: string | null
	order: number
	primary: boolean
	/** ready — готов; processing — рисуется; failed — не вышел (retry — можно повторить); missing — фото нет. */
	status: 'ready' | 'processing' | 'failed' | 'missing'
	error_code: string | null
	width: number | null
	height: number | null
	url: string | null
	preview_url: string | null
	retry: boolean
	/** Вид рисуется от главного (правка целиком): пока главный не готов, повторяется вместе с ним. */
	from_primary: boolean
	/** Входных фото у «Повторить» сверх одного: референсы правки, у «Лица» — фото-детали сборки (надбавка за каждое). */
	extra_inputs?: number
}

export interface AssetSnapshot {
	/** null — у ассета ещё нет сохранённой версии (заведётся при первом изменении). */
	id: string | null
	is_current: boolean
	origin: string | null
	prompt: string | null
	edit_prompts: string[]
	created_at: string | null
	views: AssetSnapshotView[]
	/** Пустые слоты ракурсов — дорисовываются от главного. */
	missing: AssetAngle[]
	busy: boolean
	/** Фото-детали сборки, что уйдут в «Лицо» (окно «Новый персонаж»): у его цены надбавка за каждое. */
	head_details?: number
}

export interface AssetPage extends Asset {
	snapshot: AssetSnapshot
	can_edit: boolean
}

export interface AssetHistoryItem {
	id: string
	origin: string
	is_current: boolean
	prompt: string | null
	edit_prompts: string[]
	created_at: string | null
	preview: { url: string | null; preview_url: string | null } | null
	pending: string[]
	views: number
}

export interface AssetCandidate {
	id: string
	set_id: string
	kind: AssetKind
	/** edit — правка основы в окне «Новый персонаж» (prompt — что поменять). */
	origin: 'prompt' | 'extracted' | 'edit'
	/** Вариант, от которого правка (цепочка версий окна). */
	parent_id?: string | null
	prompt: string | null
	status: 'processing' | 'ready' | 'failed'
	error_code: string | null
	width: number | null
	height: number | null
	/** Ассет уже создан из этой картинки — кнопка «Открыть». */
	character_id: string | null
	created_at: string | null
	url: string | null
	preview_url: string | null
}

export interface AssetPricing {
	/** Цена одной картинки без входных фото (кандидат по описанию). */
	image: number
	/** Надбавка за каждое входное фото — как в генераторе: референсы, основа правки, главный вид у ракурса. */
	input_photo: number
	/** Вид от главного (одно входное фото — главный); у «Лица» с фото-деталями — плюс input_photo за каждую. */
	angles: Record<string, number>
	/** Сколько дорисовок ракурсов персонажа ещё бесплатно (бесплатные персонажи тарифа). */
	free_angles_left: number
	max_candidates: number
	max_references: number
	prompt_max: number
	own_count: number
	own_limit: number
	/** Детали на фото (окно «Новый персонаж», только у персонажей): бесплатно в день, осталось, цена дальше. */
	details?: { free_per_day: number; free_left: number; price: number; enabled: boolean }
}

/** Картинка ассета с n входными фото — та же формула, что на сервере (images.get_credits_price). */
export function priceWithPhotos(p: Pick<AssetPricing, 'image' | 'input_photo'>, photos: number): number {
	return Math.round((p.image + (p.input_photo || 0) * Math.max(0, photos)) * 100) / 100
}

/** Ответ изменения: ассет (у готового — созданная копия) и версия, которую теперь смотреть. */
export interface AssetChange {
	id: string
	kind: AssetKind
	snapshot_id: string
	copied: boolean
	credits?: number
	free?: boolean
}

export interface AssetsPage {
	items: Asset[]
	pagination: { page: number; page_size: number; total: number }
}

export interface ScenePlanItem {
	position: number
	asset_id: string
	kind: AssetKind
	role: string
	photo_id: string
	url: string | null
	preview_url: string | null
}

export interface SceneWarning {
	code: string
	message: string
	blocking?: boolean
}

export interface ScenePlan {
	mode: 'unknown' | 'unsupported' | 'first_frame_only' | 'single' | 'references' | 'positional' | 'trend_slot' | string
	fits: boolean
	reason: string | null
	layout: ScenePlanItem[]
	prompt_insert: string | null
	warnings: SceneWarning[]
	/** Ассеты, чьи фото легли в слоты, и те, что остались словами (не поместились в модель). */
	used: string[]
	words_only: string[]
	/** Развилка «лист-карточка ↔ фото» для единственного слота модели (как у персонажа-одиночки). */
	choice?: { available: boolean; used: 'sheet' | 'photos'; recommended: 'sheet' | 'photos'; why: string }
	assets: Asset[]
	ignored: string[]
}

const ASSET_ERROR_TEXTS: Record<string, string> = {
	...PHOTO_ERROR_TEXTS,
	NAME_INVALID: 'Название от 1 до 60 символов',
	NOT_FOUND: 'Не нашли — возможно, уже удалено',
	NOT_OWNER: 'Это чужой аватар, локация или предмет',
	MODEL_REQUIRED: 'Сначала выберите нейросеть',
	CONSENT_REQUIRED: 'Подтвердите, что у вас есть право использовать фото человека',
	NOT_ENOUGH_CREDITS: 'Не хватает токенов — пополните баланс',
	PROMPT_EMPTY: 'Опишите, что нужно',
	PROMPT_TOO_LONG: 'Описание — до 2000 символов',
	NO_PRIMARY_VIEW: 'Сначала нужен главный вид',
	NOTHING_TO_GENERATE: 'Все ракурсы уже есть',
	SNAPSHOT_BUSY: 'Дождитесь, пока дорисуются виды',
	VOICE_UNKNOWN: 'Голос не найден — откройте список голосов и выберите ещё раз',
	NOT_CHARACTER: 'Голос бывает только у аватара',
	SECTION_CLOSED: 'Голоса пока недоступны',
	NOT_RETRYABLE: 'Повторить можно только не нарисованный вид',
	WAIT_PRIMARY: 'Этот вид рисуется от главного — сначала повторите главный',
	SNAPSHOT_NOT_READY: 'Главный вид этой версии ещё не готов',
	PRIMARY_ANGLE: 'Главный вид меняется правкой',
	VIEW_BUSY: 'Вид ещё рисуется',
	ASSET_BUSY: 'Дождитесь, пока дорисуются виды',
	CANDIDATE_NOT_READY: 'Картинка ещё не готова',
	CANDIDATE_BUSY: 'Картинка ещё рисуется',
	FRAME_NOT_READY: 'Извлечь можно из готового кадра',
	FILE_NOT_FOUND: 'Картинка-референс не найдена — загрузите заново',
	FOREIGN_PHOTO: 'Это фото не из ваших загрузок',
	SOURCE_GONE: 'Исходный файл уже удалён',
	CLOSED: 'Этот раздел пока недоступен',
}

export function parseAssetError(e: unknown): string {
	return apiErrorText(e, ASSET_ERROR_TEXTS)
}

export async function listAssets(p: {
	scope: 'library' | 'mine'
	kind?: AssetKind
	q?: string
	page?: number
	pageSize?: number
	/** Случайный, но стабильный между страницами порядок библиотеки (-1..1). */
	seed?: number
	/** Свои: updated — недавно изменённые, selected — недавно выбранные («Использовать в проекте»). */
	sort?: 'updated' | 'selected'
}): Promise<AssetsPage> {
	const q = new URLSearchParams({ scope: p.scope })
	if (p.sort) q.set('sort', p.sort)
	if (p.kind) q.set('kind', p.kind)
	if (p.q) q.set('q', p.q)
	if (p.page) q.set('page', String(p.page))
	if (p.pageSize) q.set('page_size', String(p.pageSize))
	if (p.seed != null) q.set('seed', String(p.seed))
	const page = await api.request<AssetsPage>(`/api/v1/assets?${q}`, { method: 'GET' })
	rememberAssets(page.items)
	return page
}

export async function getAsset(id: string): Promise<Asset> {
	const a = await api.request<Asset>(`/api/v1/assets/${id}`, { method: 'GET' })
	rememberAssets([a])
	return a
}

export async function planAssets(body: {
	asset_ids: string[]
	adapter?: string
	model?: string
	task: 'photo' | 'video' | 'trend'
	budget?: number
	prefer?: 'sheet' | 'photos'
}): Promise<ScenePlan> {
	const plan = await api.request<ScenePlan>('/api/v1/assets/plan', { method: 'POST', body })
	rememberAssets(plan.assets)
	return plan
}

/** «Загрузить своё» (Upload your own у Artlist): картинка становится главным видом, без генерации. У персонажа —
 *  с подтверждением права на фото человека. */
export async function uploadAsset(kind: AssetKind, name: string, file: File, consent = false): Promise<Asset> {
	const fd = new FormData()
	fd.append('kind', kind)
	fd.append('name', name)
	fd.append('consent', String(consent))
	fd.append('file', file)
	const a = await postForm<Asset>('/api/v1/assets/upload', fd)
	invalidateMentionAssets()
	return a
}

/** Прежний вход пикера: своя локация или предмет из фото. */
export async function createOwnAsset(kind: 'location' | 'prop', name: string, file: File): Promise<Asset> {
	return uploadAsset(kind, name, file)
}

/** «Сохранить как персонажа» из картинки в слоте генератора (свой файл — проверяет сервер). */
export async function assetFromSlot(body: { kind: AssetKind; name: string; source_url: string; consent: boolean }): Promise<Asset> {
	const a = await api.request<Asset>('/api/v1/assets/from-slot', { method: 'POST', body })
	invalidateMentionAssets()
	return a
}

export async function deleteOwnAsset(id: string): Promise<void> {
	await api.request(`/api/v1/assets/${id}`, { method: 'DELETE' })
	invalidateMentionAssets()
}

// ── Страница ассета (как у Artlist: виды, версии, правка) ────────────────

export async function getAssetPage(id: string, snapshotId?: string | null): Promise<AssetPage> {
	const q = new URLSearchParams({ page: 'true' })
	if (snapshotId) q.set('snapshot_id', snapshotId)
	const a = await api.request<AssetPage>(`/api/v1/assets/${id}?${q}`, { method: 'GET' })
	rememberAssets([a])
	return a
}

export async function getAssetHistory(id: string, page = 1): Promise<{ items: AssetHistoryItem[]; pagination: AssetsPage['pagination'] }> {
	return api.request(`/api/v1/assets/${id}/history?page=${page}`, { method: 'GET' })
}

/** «Сохранить изменения»: просматриваемая версия становится текущей. */
export async function saveAssetSnapshot(id: string, snapshotId: string): Promise<void> {
	await api.request(`/api/v1/assets/${id}/snapshots/${snapshotId}/current`, { method: 'POST' })
	invalidateMentionAssets()
}

export async function renameAsset(id: string, name: string): Promise<Asset> {
	const a = await api.request<Asset>(`/api/v1/assets/${id}`, { method: 'PATCH', body: { name } })
	rememberAssets([a])
	invalidateMentionAssets()
	return a
}

export async function duplicateAsset(id: string): Promise<{ id: string; kind: AssetKind }> {
	const r = await api.request<{ id: string; kind: AssetKind }>(`/api/v1/assets/${id}/duplicate`, { method: 'POST' })
	invalidateMentionAssets()
	return r
}

/** «Использовать в проекте»: поднять в «недавно выбранные». */
export async function markAssetSelected(id: string): Promise<void> {
	await api.request(`/api/v1/assets/${id}/selected`, { method: 'POST' })
}

export async function generateAssetAngles(id: string, body: { snapshot_id?: string | null; angles?: AssetAngle[]; expected_credits?: number }): Promise<AssetChange> {
	return api.request<AssetChange>(`/api/v1/assets/${id}/angles/generate`, { method: 'POST', body })
}

export async function uploadAssetAngle(id: string, angle: AssetAngle, file: File, snapshotId?: string | null, consent = false): Promise<AssetChange> {
	const fd = new FormData()
	fd.append('angle', angle)
	if (snapshotId) fd.append('snapshot_id', snapshotId)
	fd.append('consent', String(consent))
	fd.append('file', file)
	return postForm<AssetChange>(`/api/v1/assets/${id}/angles/upload`, fd)
}

export async function deleteAssetAngle(id: string, photoId: string, snapshotId?: string | null): Promise<AssetChange> {
	const q = snapshotId ? `?snapshot_id=${encodeURIComponent(snapshotId)}` : ''
	return api.request<AssetChange>(`/api/v1/assets/${id}/angles/${photoId}${q}`, { method: 'DELETE' })
}

export interface AssetEditBody {
	snapshot_id?: string | null
	prompt: string
	reference_file_ids?: string[]
	consent?: boolean
	expected_credits?: number
}

/** «Опишите, что изменить» — новая версия всех видов. */
export async function editAsset(id: string, body: AssetEditBody): Promise<AssetChange> {
	return api.request<AssetChange>(`/api/v1/assets/${id}/edit`, { method: 'POST', body })
}

/** «Опишите, как изменить этот ракурс» — новая версия с одним заменённым видом. */
export async function editAssetAngle(id: string, photoId: string, body: AssetEditBody): Promise<AssetChange> {
	return api.request<AssetChange>(`/api/v1/assets/${id}/angles/${photoId}/edit`, { method: 'POST', body })
}

export async function retryAssetAngle(id: string, photoId: string, body: { snapshot_id?: string | null; expected_credits?: number }): Promise<AssetChange> {
	return api.request<AssetChange>(`/api/v1/assets/${id}/angles/${photoId}/retry`, { method: 'POST', body })
}

export async function getAssetPricing(kind: AssetKind): Promise<AssetPricing> {
	return api.request<AssetPricing>(`/api/v1/assets/pricing?kind=${kind}`, { method: 'GET' })
}

// ── Кандидаты по описанию («История» раздела) ─────────────────────────────

export async function listAssetCandidates(kind: AssetKind, page = 1): Promise<{ items: AssetCandidate[]; pagination: AssetsPage['pagination']; busy: boolean }> {
	return api.request(`/api/v1/assets/creation-sets?kind=${kind}&page=${page}`, { method: 'GET' })
}

export async function createAssetCandidates(body: {
	kind: AssetKind
	prompt: string
	count: number
	reference_file_ids?: string[]
	consent?: boolean
	expected_credits?: number
}): Promise<{ set_id: string; count: number; credits: number }> {
	return api.request('/api/v1/assets/creation-sets', { method: 'POST', body })
}

/** Кандидат из готового кадра «Сцены» (у Artlist — «@» на кадре). */
export async function extractAssetFromFrame(body: { kind: AssetKind; version_id: string; hint?: string; consent?: boolean; expected_credits?: number }): Promise<{ set_id: string; count: number; credits: number }> {
	return api.request('/api/v1/assets/extract', { method: 'POST', body })
}

export async function createAssetFromCandidate(candidateId: string, name?: string): Promise<{ id: string; kind: AssetKind; existing: boolean; asset?: Asset }> {
	const r = await api.request<{ id: string; kind: AssetKind; existing: boolean; asset?: Asset }>(`/api/v1/assets/candidates/${candidateId}/create`, { method: 'POST', body: { name: name || null } })
	invalidateMentionAssets()
	return r
}

// ── Окно «Новый персонаж» ─────────────────────────────────────────────────

/** Деталь на фото: вид (внутренний, для промпта) и слова, которые видит и может поправить человек. */
export interface DetailItem {
	kind: string
	label: string
}

/** Фото-деталь сборки: файл и что с него взять (пусто — фото целиком). */
export interface CreatorReference {
	file_id: string
	items: DetailItem[]
}

/** Что можно перенести с фото на персонажа: первые в день — бесплатно (expected 0), дальше — по цене на кнопке. */
export async function recognizeDetails(fileId: string, expectedCredits: number): Promise<{ items: DetailItem[]; credits: number; free_left: number; price: number }> {
	return api.request('/api/v1/assets/details', { method: 'POST', body: { file_id: fileId, expected_credits: expectedCredits } })
}

/** Варианты окна: собрать (фото-детали и/или описание) или поправить основу — свой вариант, своё фото или картинку. */
export async function createCreatorSet(body: {
	prompt: string
	count: number
	references?: CreatorReference[]
	base_candidate_id?: string
	base_file_id?: string
	base_source_url?: string
	consent?: boolean
	expected_credits?: number
}): Promise<{ set_id: string; count: number; credits: number }> {
	return api.request('/api/v1/assets/creation-sets', { method: 'POST', body: { kind: 'character', ...body } })
}

export async function getCreationSet(setId: string): Promise<{ items: AssetCandidate[]; busy: boolean }> {
	return api.request(`/api/v1/assets/creation-sets/${setId}`, { method: 'GET' })
}

export async function deleteAssetCandidate(candidateId: string): Promise<void> {
	await api.request(`/api/v1/assets/candidates/${candidateId}`, { method: 'DELETE' })
}

type ThumbSource = Pick<Asset, 'avatar_preview_url' | 'avatar_url'> & Partial<Pick<Asset, 'kind' | 'is_stock' | 'views' | 'avatar_tile_url'>>

/** Роль главного вида (он же avatar_url): 'front' — в рост; 'headshot' / 'upper' — портрет (у готового со своей
 *  картинкой-портретом). null — виды не переданы (кружки связей). */
function primaryRole(a: ThumbSource): string | null {
	return a.views?.find((v) => v.url && v.url === a.avatar_url)?.role ?? null
}

/** Вид «Лицо» аватара — для чипов и меню «@»; null — лица ещё нет или это не аватар. Готовые — так же, как свои
 *  (владелец 13.10: «а персонажи общие тоже будут не в полный рост?»). */
export function tileFace(a: Partial<Pick<Asset, 'kind' | 'is_stock' | 'views'>>): AssetView | null {
	if (a.kind !== 'character') return null
	return a.views?.find((v) => v.role === 'headshot' && (v.preview_url || v.url)) ?? null
}

/** Превью ассета для плиток и чипов: лёгкое, если есть. У аватара — вид «Лицо»: в чипах и пикерах портрет по плечи,
 *  как у Artlist, а рост — на странице аватара (владелец 13.10: «на главных экранах все в полный рост»). */
export const assetThumb = (a: ThumbSource): string | null => {
	const face = tileFace(a)
	return face ? face.preview_url || face.url : a.avatar_preview_url || a.avatar_url
}

/** Плитка галереи и пикера. Аватар (свой и готовый) — верх его полноростового кадра: голова с воздухом сверху, плечи
 *  и грудь, как плитки Artlist (владелец 13.10: «Лицо» крупно — «может нужно немного дальше?»). Картинка — плитка
 *  своим файлом (сервер режет этот верх в родном разрешении: резко при весе превью ~20 КБ, владелец 13.10: «качество
 *  превью улучшить, но не утяжелять»); у старых видов без неё — превью (800px) с CSS-обрезкой, top — плитке нужен класс
 *  обрезки. Полный файл в плитки не берём: витрина с десятками плиток стала бы тяжёлой. Главный вид-портрет (готовый
 *  со своей картинкой), локации и предметы — как есть. */
export function tileCrop(a: ThumbSource & Partial<Pick<Asset, 'width' | 'height'>>): { src: string | null; top: boolean } {
	const role = primaryRole(a)
	const full = a.kind === 'character' && role !== 'headshot' && role !== 'upper' && !!a.width && !!a.height && a.height > a.width
	if (full && a.avatar_tile_url) return { src: a.avatar_tile_url, top: false }
	if (full && (a.avatar_preview_url || a.avatar_url)) return { src: a.avatar_preview_url || a.avatar_url, top: true }
	return { src: assetThumb(a), top: false }
}

/** Главный вид крупно (клик по плитке витрины): у аватара — «в рост», даже если главный у готового — портрет. */
export function fullViewUrl(a: Pick<Asset, 'avatar_url' | 'avatar_preview_url'> & Partial<Pick<Asset, 'kind' | 'views'>>): string | null {
	const front = a.kind === 'character' ? a.views?.find((v) => v.role === 'front' && v.url)?.url : null
	return front || a.avatar_url || a.avatar_preview_url
}

// ── Имена ассетов ───────────────────────────────────────────────────────
// Всё, что вкладка уже видела (пикер, меню «@», план сцены): по ним упоминание @{uuid}@ превращается в имя там,
// где промпт показывают человеку (карточка результата, «Копировать промпт»).
const assetNames = new Map<string, string>()

function rememberAssets(list: Pick<Asset, 'id' | 'name'>[]): void {
	for (const a of list) if (a?.id && a.name) assetNames.set(a.id.toLowerCase(), a.name)
}

export function knownAssetNames(): Record<string, string> {
	return Object.fromEntries(assetNames)
}

// ── Список для меню «@» ─────────────────────────────────────────────────
// Свои — только готовые (черновик персонажа в генерацию не идёт), дальше первые 60 библиотечных; остальное меню
// ищет по названию на сервере. Кэш живёт пять минут (за это время ассет могли создать в другой вкладке или в
// кабинете персонажей) и сбрасывается при смене аккаунта: имена и лица чужих персонажей следующему не достаются.
const MENTION_TTL_MS = 5 * 60_000
let mentionCache: { list: Asset[]; at: number; complete: boolean } | null = null
let mentionLoading: Promise<Asset[]> | null = null
let mentionGen = 0

export async function loadMentionAssets(): Promise<Asset[]> {
	if (mentionCache && Date.now() - mentionCache.at < MENTION_TTL_MS) return mentionCache.list
	if (!mentionLoading) {
		const gen = mentionGen
		const job = Promise.all([listAssets({ scope: 'mine', pageSize: 60 }), listAssets({ scope: 'library', pageSize: 60 })])
			.then(([m, l]) => {
				const own = m.items.filter((a) => a.status === 'ready')
				const seen = new Set(own.map((a) => a.id))
				const list = [...own, ...l.items.filter((a) => !seen.has(a.id))]
				// Кэш сбросили, пока шёл запрос (сменился аккаунт, создали ассет) — ответ прежнего не запоминаем.
				// complete — в списке всё, что есть у сервера: поиск по названию тогда идёт по месту, без запросов.
				const complete = m.pagination.total <= m.items.length && l.pagination.total <= l.items.length
				if (gen === mentionGen) mentionCache = { list, at: Date.now(), complete }
				return list
			})
			.finally(() => { if (mentionLoading === job) mentionLoading = null })
		mentionLoading = job
	}
	return mentionLoading
}

/** Список из кэша — чтобы меню открылось сразу; устаревший тоже годится (свежий дотянет loadMentionAssets). */
export function peekMentionAssets(): Asset[] | null {
	return mentionCache?.list ?? null
}

/** В кэше меню весь список (своих и библиотечных не больше страницы) — искать на сервере незачем. */
export function mentionListComplete(): boolean {
	return !!mentionCache?.complete
}

/** Сбросить кэш меню «@» (создали или удалили свой ассет). */
export function invalidateMentionAssets(): void {
	mentionGen++
	mentionCache = null
	mentionLoading = null
}

onSceneUserChange(() => {
	invalidateMentionAssets()
	assetNames.clear()
})

// ── Голос персонажа (как у Artlist: панель «Голоса» на странице персонажа) ──

export interface AssetVoiceChange {
	id: string
	kind: AssetKind
	/** Старые серверы: выбор голоса у готового создавал копию — страница переходила на неё. Теперь всегда false. */
	copied: boolean
	voice: VoiceCard | null
	voice_auto: boolean
	/** Готовый персонаж: голос выбран лично; stock_voice — голос библиотеки, к которому можно вернуться. */
	voice_chosen?: boolean
	stock_voice?: VoiceCard | null
}

/** Голос персонажа (из каталога GET /voice/catalog); null — убрать. У готового персонажа выбор личный: персонаж в
 *  библиотеке не меняется, копии нет (владелец 07.10); null у готового — вернуть голос библиотеки. */
export async function setAssetVoice(id: string, voiceId: string | null): Promise<AssetVoiceChange> {
	return api.request<AssetVoiceChange>(`/api/v1/assets/${id}/voice`, { method: 'PUT', body: { voice_id: voiceId } })
}

/** Подобрать своему персонажу голос под пол и возраст на вид (бесплатно); голос уже есть — вернётся он. */
export async function autoAssetVoice(id: string): Promise<{ voice: VoiceCard | null; voice_auto: boolean }> {
	return api.request(`/api/v1/assets/${id}/voice/auto`, { method: 'POST' })
}

/** Связать ассет с другим (оба доступны, хотя бы один — свой); ответ — связи ассета. */
export async function addAssetLink(id: string, linkedId: string): Promise<{ links: AssetLink[] }> {
	return api.request<{ links: AssetLink[] }>(`/api/v1/assets/${id}/links`, { method: 'POST', body: { linked_id: linkedId } })
}

export async function removeAssetLink(id: string, linkedId: string): Promise<{ links: AssetLink[] }> {
	return api.request<{ links: AssetLink[] }>(`/api/v1/assets/${id}/links/${linkedId}`, { method: 'DELETE' })
}
